import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Image } from "@/components/common/image";
import {
  ChevronRight,
  UserCircle,
  PlayCircle,
  Radio,
  Globe,
  ImageIcon,
  FileText,
  Bookmark,
} from "lucide-react";
import { formatDateSmart } from "@/utils/date-formatter";
import { agentUserInfoHooks } from "@/manager/manager/agent-user-info-manager";
import { ModalCloseButton } from "@/components/common/modal-close-button";
import { AgentData } from "@/services/agent-list";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import BookmarkIcon from "../ui/icon/icon/bookmark-icon";
import { ArticleIcon, MomentsIcon, PodcastIcon, PostIcon, UserIcon, VideoIcon } from "./assets";

export type PanelSectionKey =
  | "personal"
  | "video"
  | "podcast"
  | "moments"
  | "post"
  | "article";

export interface PanelAgentDetailProps {
  open: boolean;
  expireAt?: string;
  videoSubscribe?: boolean;
  podcastSubscribe?: boolean;
  momentsSubscribe?: boolean;
  graphicSubscribe?: boolean;
  articleSubscribe?: boolean;
  agent: AgentData;
  onOpenChange: (open: boolean) => void;
  onNavigate?: (key: "personal" | "video" | "podcast" | "moments" | "post" | "article") => void;
}

export const PanelAgentDetail: React.FC<PanelAgentDetailProps> = ({
  open,
  expireAt,
  videoSubscribe,
  podcastSubscribe,
  momentsSubscribe,
  graphicSubscribe,
  articleSubscribe,
  agent = {} as AgentData,
  onOpenChange,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const handleGo = (key: PanelSectionKey) => onNavigate?.(key);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {/* 半透明遮罩 */}
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 panel-overlay-animation" />

        {/* 右侧滑入面板 */}
        <Dialog.Content
          className="
            fixed right-0 top-0 z-50 h-full w-[480px]
            bg-[rgba(249,250,251,1)]
            flex flex-col
            panel-content-animation
          "
        >
          <ModalCloseButton
            className="absolute top-6 right-6"
            onClick={() => onOpenChange(false)}
          />
          {/* 顶部头像、名称、简介 */}
          <div className="flex flex-col items-center pt-20 pb-6">
            <div className="w-20 h-20 rounded-full overflow-hidden flex items-center justify-center">
              <Image
                src={agent?.avatar}
                alt="agent-avatar"
                className="w-full h-full object-cover rounded-full"
              />
            </div>
            <div className="mt-5 px-6 text-xl font-semibold text-[rgba(24,29,39,1)]">
              {agent?.name}
            </div>
            <div className="mt-1.5 px-6 text-sm text-[rgba(102,112,133,1)] text-center line-clamp-5">
              {agent?.intro}
            </div>
          </div>

          {/* 内容可滚动 */}
          <div className="flex-1 overflow-auto px-6">
            {/* 个人信息 */}
            <div className="rounded-xl bg-white">
              <button
                className="w-full flex items-center justify-between px-5 h-[61px]"
                onClick={() => handleGo("personal")}
              >
                <div className="flex items-center gap-2">
                  <UserIcon className="w-5 h-5 text-[rgba(16,24,40,1)]" />
                  <span className="text-[15px] text-[rgba(16,24,40,1)] font-medium">{t("agent_creation.agent_modal.personal_info")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Image src={agent?.subAvatar || ""} className="w-6 h-6 rounded-full object-cover" alt={agent?.subName || ""} />
                  <span className="text-[15px] text-[rgba(102,112,133,1)] font-medium">{agent?.subName || ""}</span>
                  <ChevronRight className="w-5 h-5 text-[rgba(152,162,179,1)]" />
                </div>
              </button>
            </div>

            {(videoSubscribe || podcastSubscribe || momentsSubscribe || graphicSubscribe || articleSubscribe) && (
              <div className="mt-5 mb-4 mx-4 text-sm text-[rgba(102,112,133,1)] font-medium">{t("agent_creation.agent_modal.subscription_settings")}</div>
            )}
            {(videoSubscribe || podcastSubscribe || momentsSubscribe || graphicSubscribe || articleSubscribe) && 
              <div className="rounded-xl bg-white">
                {[
                  {
                    key: "video",
                    label: t("agent_creation.video"),
                    Icon: VideoIcon,
                    isSubscribe: videoSubscribe,
                  },
                  {
                    key: "podcast",
                    label: t("agent_creation.podcast"),
                    Icon: PodcastIcon,
                    isSubscribe: podcastSubscribe,
                  },
                  {
                    key: "moments",
                    label: t("agent_creation.moments"),
                    Icon: MomentsIcon,
                    isSubscribe: momentsSubscribe,
                  },
                  {
                    key: "post",
                    label: t("agent_creation.post"),
                    Icon: PostIcon,
                    isSubscribe: graphicSubscribe,
                  },
                  {
                    key: "article",
                    label: t("agent_creation.article"),
                    Icon: ArticleIcon,
                    isSubscribe: articleSubscribe,
                  },
                ].map(({ key, label, Icon, isSubscribe }, idx, arr) => isSubscribe && (
                  <button
                    key={key}
                    className={`w-full flex items-center justify-start gap-3 px-5`}
                    onClick={() => handleGo(key as PanelSectionKey)}
                  >
                    <Icon className="w-5 h-5 text-[rgba(16,24,40,1)]" />
                    <div className={`flex-1 flex justify-between items-center gap-3 h-[61px] ${idx < arr.length - 1 ? "border-b border-[rgba(234,236,240,1)]" : ""
                      }`}>
                      <span className="text-[15px] text-[rgba(16,24,40,1)] font-medium">{label}</span>
                      <ChevronRight className="w-5 h-5 text-[rgba(152,162,179,1)]" />
                    </div>
                  </button>
                ))}
              </div>
            }
          </div>

          {/* 底部固定到期时间 */}
          <div className="shrink-0 py-8 px-6 flex items-center justify-center gap-2">
            <BookmarkIcon className="w-5 h-5 text-[rgba(102,112,133,1)]" />
            <span className="text-sm text-[rgba(102,112,133,1)] font-medium">
              {expireAt
                ? `${dayjs(expireAt).format("YYYY-MM-DD")} ${t("agent_creation.expired")}`
                : ""}
            </span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
